import { useTranslation } from 'react-i18next';
import React, { useContext, useEffect, useMemo } from 'react';

import { UserEarnDetailResponse } from '@utx/utx-client';
import { CurrenciesContext } from '@/currencies/currency.context';

import { formatAmountByDecimals } from '../../../../packages/utils/numberFormatting';
import { formatDate } from '../../../../packages/utils/dateFormatting';
import { ProgressStatus } from '@/design-system/components/progress/ProgressItem';
import { GetWalletsByAssetResponseWalletsItem, UserSubscriptionPreviewResponse, useUtxEarnSubscriptionsSubscribe } from '@utx/utx-client';
import { getEarnTenorTranslationKey } from '../../utils/getEarnTenorTranslationKey';
import { EarnSubscriptionResponse, UserSubscriptionRequest } from '../../../../../../packages/utx-client/src/client';
import { useNavigation } from '@react-navigation/native';
import { SubscribeStackParamList, SubscribeStackRoutes } from './type';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { View } from 'dripsy';
import { TextBody } from '@/design-system/components/typography';

interface UseExecuteSubscriptionParams {
  onLayout: (event: any) => void;
  requestParams: UserSubscriptionRequest;
  account: GetWalletsByAssetResponseWalletsItem;
  assetDetails?: UserEarnDetailResponse;
  preview?: UserSubscriptionPreviewResponse;
}

export const useExecuteSubscription = (params: UseExecuteSubscriptionParams) => {
  const { t } = useTranslation('earn');
  const { getDecimalByAsset } = useContext(CurrenciesContext);
  const { requestParams, assetDetails, preview, account } = params;
  const navigation = useNavigation<NativeStackNavigationProp<SubscribeStackParamList>>();

  const mutate = useUtxEarnSubscriptionsSubscribe();
  const data = mutate.data?.data as EarnSubscriptionResponse;

  const progressCardData = useMemo(() => {
    if (!data) return [];

    let result: Array<ProgressStatus> = [
      {
        name: t('earn-invest::glossary.subscription'),
        date: formatDate(data.subscriptionDate),
        status: 'active'
      },
      {
        name: t('earn-invest::glossary.first-reward'),
        date: formatDate(data.firstRewardAccrualDate),
        status: 'inactive'
      }
    ];

    if ('maturityDate' in data && data.maturityDate !== null) {
      result.push({
        name: t('earn-invest::glossary.maturity'),
        date: formatDate(data.maturityDate),
        status: 'inactive'
      });
    }
    return result;
  }, [mutate]);

  const textDetailsCardData = useMemo(() => {
    if (!data) return [];

    const tenorKey = getEarnTenorTranslationKey(assetDetails.tenor || null);

    let result = [
      {
        name: t('earn-invest::glossary.account'),
        data: '',
        status: 'default',
        render: () => {
          return (
            <View sx={{ alignItems: 'flex-end' }}>
              <TextBody>
                {account.accountLabel ? t(`earn-invest::glossary.${account.accountLabel}`) : account.accountName || ' - '}
              </TextBody>
              <TextBody>{account.accountNumber}</TextBody>
            </View>
          );
        }
      },
      {
        name: t('earn-invest::glossary.instruction-time'),
        data: data.instructionTime ? formatDate(data?.instructionTime) : ' - '
      },
      {
        name: t('earn-invest::glossary.ref-id'),
        data: data.refId || ' - '
      }
    ];

    return result;
  }, [mutate]);

  const execute = () => {
    mutate.mutate({
      data: {
        offerId: requestParams.offerId,
        walletId: requestParams.walletId,
        amount: `${requestParams.amount}`,
        asset: requestParams.asset,
        autoRenew: requestParams.autoRenew
      }
    });
  };

  useEffect(() => {
    if (mutate.isError || !mutate.isSuccess) return;
    navigation.replace(SubscribeStackRoutes.Result, {
      offerId: requestParams.offerId,
      offer: assetDetails,
      preview,
      account,
      onLayout: params.onLayout,
      asset: requestParams.asset,
      offerName: mutate.data.data.offerName,
      estimatedApy: assetDetails?.estimatedApy,
      subscriptionAmount: requestParams.amount,
      progressCardData,
      textDetailsCardData,
      closeModal: () => {}
    });
  }, [mutate.isSuccess]);

  const errorText = useMemo(() => {
    if (!mutate.isError) return null;
    return t('earn-invest::glossary.error-unable-to-subscribe');
  }, [mutate.error]);

  return {
    ...mutate,
    execute,
    subscriptionAmount: formatAmountByDecimals({
      value: data?.amount || '',
      decimals: getDecimalByAsset(data?.asset)
    }),
    asset: data?.asset || '',
    progressCardData,
    textDetailsCardData,
    errorText
  };
};
